import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader2, Mail, Globe, Lock, CheckCircle2, Cookie, Clock, AlertTriangle, Scan, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── useWindowWidth ───────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

// ── Load Razorpay SDK ────────────────────────────────────────────────
function loadRazorpaySDK() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onerror = () => resolve(false);
    s.onload = () => {
      let attempts = 0;
      const check = setInterval(() => {
        attempts++;
        if (window.Razorpay) {
          clearInterval(check);
          resolve(true);
        } else if (attempts >= 30) {
          clearInterval(check);
          resolve(false);
        }
      }, 100);
    };
    document.body.appendChild(s);
  });
}

const CheckItem = ({ icon, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
    <div style={{ color: '#00FF94', flexShrink: 0 }}>{icon}</div>
    <span style={{ fontSize: 13, color: '#b0b8d0' }}>{label}</span>
  </div>
);

const StepDot = ({ n, label, active, done, isMobile }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 7, flex: 1 }}>
    <div style={{
      width: isMobile ? 26 : 28, height: isMobile ? 26 : 28,
      borderRadius: '50%', flexShrink: 0,
      background: done ? '#00FF94' : active ? '#7000FF' : 'transparent',
      border: `2px solid ${done ? '#00FF94' : active ? '#00F0FF' : '#2a2a4a'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: isMobile ? 11 : 12,
      color: done ? '#020210' : active ? '#fff' : '#4a4a6a',
    }}>
      {done ? '✓' : n}
    </div>
    <span style={{
      fontSize: isMobile ? 11 : 12,
      fontWeight: active || done ? 700 : 400,
      color: active ? '#00F0FF' : done ? '#00FF94' : '#4a4a6a',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  </div>
);

const ScannerPage = () => {
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;

  const [url, setUrl]           = useState('');
  const [email, setEmail]       = useState('');
  const [urlError, setUrlError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [scanId, setScanId]     = useState(null);

  useEffect(() => { loadRazorpaySDK(); }, []);

  const validate = () => {
    let ok = true;
    if (!url.trim()) {
      setUrlError('Please enter a website URL'); ok = false;
    } else {
      try { new URL(url.startsWith('http') ? url : 'https://' + url); setUrlError(''); }
      catch { setUrlError('Please enter a valid URL (e.g. https://example.com)'); ok = false; }
    }
    if (!email.trim()) {
      setEmailError('Email is required — your report will be sent here'); ok = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address'); ok = false;
    } else {
      setEmailError('');
    }
    return ok;
  };

  const handleCreateScan = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/scan`, {
        url: url.startsWith('http') ? url : 'https://' + url, email,
      });
      setScanId(res.data.id);
      setShowPayment(true);
      toast.success('Details confirmed! Complete payment to start your scan.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create scan. Please try again.');
    } finally { setLoading(false); }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // const orderRes = await axios.post(`${API}/create-payment-order`, { amount: 100, currency: 'INR', scan_id: scanId });
      const orderRes = await axios.post(`${API}/create-payment-order`, { amount: 29900, currency: 'INR', scan_id: scanId });
      const { order_id, amount, currency, key_id } = orderRes.data;
      const sdkLoaded = await loadRazorpaySDK();

      if (!sdkLoaded || !window.Razorpay) {
        await simulatePayment(order_id); return;
      }

      const options = {
        key: key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount, currency,
        name: 'WebShield Security Scanner',
        description: 'Full 8-Protocol Website Security Scan',
        order_id,
        prefill: { email },
        theme: { color: '#7000FF' },
        modal: { ondismiss: () => setLoading(false) },
        handler: async (response) => { await verifyAndRedirect(response, order_id); },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error('Payment failed: ' + (resp.error?.description || 'Unknown error'));
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment initialisation failed.');
      setLoading(false);
    }
  };

  const verifyAndRedirect = async (rzpResponse, orderId) => {
    try {
      await axios.post(`${API}/verify-payment`, {
        razorpay_order_id:   orderId || rzpResponse.razorpay_order_id,
        razorpay_payment_id: rzpResponse.razorpay_payment_id,
        razorpay_signature:  rzpResponse.razorpay_signature || 'test_sig',
        scan_id: scanId,
      });
      toast.success('Payment verified! Your security scan has started.');
      navigate(`/results/${scanId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment verification failed.');
    } finally { setLoading(false); }
  };

  const simulatePayment = async (orderId) => {
    try {
      toast('Test mode: simulating payment…', { icon: '🧪' });
      await new Promise(r => setTimeout(r, 800));
      await verifyAndRedirect({ razorpay_payment_id: 'pay_test_' + Date.now(), razorpay_signature: 'test_sig' }, orderId);
    } catch { toast.error('Simulated payment failed'); setLoading(false); }
  };

  const checks = [
    { icon: <Lock size={14} />,          label: 'SSL / HTTPS Certificate'  },
    { icon: <Shield size={14} />,        label: 'HTTP Security Headers'     },
    { icon: <Scan size={14} />,          label: 'Open Port Scan'            },
    { icon: <Globe size={14} />,         label: 'DNS Configuration'         },
    { icon: <Zap size={14} />,           label: 'Technology Stack'          },
    { icon: <AlertTriangle size={14} />, label: 'Malware / Blacklist Check' },
    { icon: <Clock size={14} />,         label: 'Response Performance'      },
    { icon: <Cookie size={14} />,        label: 'Cookie Security'           },
  ];

  const inputStyle = (err) => ({
    width: '100%', boxSizing: 'border-box',
    background: '#020210',
    border: `1px solid ${err ? '#FF0055' : '#2a2a4a'}`,
    borderRadius: 7,
    padding: isMobile ? '11px 13px' : isTablet ? '11px 13px' : '10px 13px',
    color: '#fff',
    fontSize: isMobile ? 15 : 14,
    outline: 'none', WebkitAppearance: 'none',
  });

  // Layout values – slightly reduced when payment is shown to fit perfectly
  const containerPadding = isMobile ? '10px 12px' : isTablet ? '14px 18px' : '16px 22px';
  const logoMarginBottom = showPayment ? (isMobile ? 8 : 10) : (isMobile ? 12 : 14);
  const stepMarginBottom = showPayment ? (isMobile ? 10 : 12) : (isMobile ? 14 : 16);
  const cardPadding = showPayment
    ? (isMobile ? '14px 14px' : isTablet ? '16px 18px' : '18px 20px')
    : (isMobile ? '18px 16px' : isTablet ? '22px 20px' : '24px 24px');

  return (
    <div className="min-h-screen bg-background" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: containerPadding,
      minHeight: '100vh',
      maxHeight: '100vh',
      overflow: 'hidden',
    }}>

      {/* Background glows */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: 'radial-gradient(circle at 25% 20%,rgba(0,240,255,0.08) 0%,transparent 45%),radial-gradient(circle at 75% 75%,rgba(112,0,255,0.08) 0%,transparent 45%)'
      }} />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: isMobile ? '100%' : isTablet ? 540 : 580 }}>

        {/* Logo */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', marginBottom: logoMarginBottom }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginBottom: 7, cursor: 'pointer' }}
            onClick={() => navigate('/')}>
            <Shield size={isMobile ? 26 : 30} color="#00F0FF" />
            <span className="font-heading" style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: '#fff' }}>
              Web<span style={{ color: '#00F0FF' }}>Shield</span>
            </span>
          </div>
          <p style={{ color: '#b0b8d0', fontSize: isMobile ? 12 : 13, maxWidth: 360, margin: '0 auto' }}>
            {showPayment ? 'Complete payment to unlock your security report' : 'Enter website details for comprehensive analysis'}
          </p>
        </motion.div>

        {/* Step indicator */}
        <div style={{
          display: 'flex', gap: 8, marginBottom: stepMarginBottom,
          background: '#0d0d2b', borderRadius: 12,
          padding: isMobile ? '11px 13px' : isTablet ? '13px 15px' : '14px 17px',
          border: '1px solid #1a1a3e',
        }}>
          <StepDot n={1} label="Enter Details" active={!showPayment} done={showPayment} isMobile={isMobile} />
          <div style={{ width: 1, background: '#1a1a3e', flexShrink: 0 }} />
          <StepDot n={2} label={isMobile ? "Pay ₹299" : "Pay ₹299"} active={showPayment} done={false} isMobile={isMobile} />
          <div style={{ width: 1, background: '#1a1a3e', flexShrink: 0 }} />
          <StepDot n={3} label="Results" active={false} done={false} isMobile={isMobile} />
        </div>

        <AnimatePresence mode="wait">

          {/* ── STEP 1: Details ── */}
          {!showPayment && (
            <motion.div key="details"
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.2 }}
              className="glass-card" style={{ padding: cardPadding }}
            >
              {/* URL */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#00F0FF', marginBottom: 6 }}>
                  <Globe size={14} /> Website URL
                </label>
                <input
                  type="url" inputMode="url" autoComplete="url"
                  value={url} onChange={e => { setUrl(e.target.value); setUrlError(''); }}
                  placeholder="https://example.com"
                  data-testid="url-input"
                  style={inputStyle(urlError)}
                />
                {urlError && <p style={{ color: '#FF0055', fontSize: 11, marginTop: 4 }}>{urlError}</p>}
              </div>

              {/* Email */}
              <div style={{ marginBottom: isMobile ? 18 : 22 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#00F0FF', marginBottom: 6 }}>
                  <Mail size={14} /> Email Address
                </label>
                <input
                  type="email" inputMode="email" autoComplete="email"
                  value={email} onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                  placeholder="you@example.com"
                  data-testid="email-input"
                  style={inputStyle(emailError)}
                />
                {emailError
                  ? <p style={{ color: '#FF0055', fontSize: 11, marginTop: 4 }}>{emailError}</p>
                  : <p style={{ color: '#d3d3e1', fontSize: 11, marginTop: 4 }}>PDF report sent to this address</p>
                }
              </div>

              {/* What we check */}
              <div style={{
                background: '#020210', border: '1px solid #1a1a3e',
                borderRadius: 10, padding: isMobile ? '13px 14px' : '15px 17px',
                marginBottom: isMobile ? 18 : 22,
              }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 10 }}>What we'll check:</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                  {checks.map((c, i) => <CheckItem key={i} icon={c.icon} label={c.label} />)}
                </div>
              </div>

              <button onClick={handleCreateScan} disabled={loading} className="btn-primary"
                data-testid="create-scan-button"
                style={{
                  width: '100%', fontSize: isMobile ? 14 : 15,
                  padding: isMobile ? '11px 0' : isTablet ? '13px 0' : '12px 0',
                  opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                {loading ? <><Loader2 size={17} className="animate-spin" />Creating scan…</> : 'Continue to Payment →'}
              </button>
            </motion.div>
          )}

          {/* ── STEP 2: Payment (optimised for no-scroll fit) ── */}
          {showPayment && (
            <motion.div key="payment"
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.2 }}
              className="glass-card" style={{ padding: cardPadding }}
            >
              {/* Header – compact */}
              <div style={{ textAlign: 'center', marginBottom: isMobile ? 12 : 14 }}>
                <div style={{
                  width: isMobile ? 48 : 52, height: isMobile ? 48 : 52, borderRadius: '50%',
                  margin: '0 auto 10px',
                  background: 'rgba(112,0,255,0.2)', border: '2px solid rgba(0,240,255,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Shield size={isMobile ? 22 : 26} color="#00F0FF" />
                </div>
                <h2 className="font-heading" style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, marginBottom: 3, color: '#fff' }}>
                  Secure Payment
                </h2>
                <p style={{ color: '#b0b8d0', fontSize: 12 }}>
                  Scan starts immediately after payment
                </p>
              </div>

              {/* Scan summary – smaller margins */}
              <div style={{ background: '#020210', border: '1px solid #1a1a3e', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 10, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 1 }}>Scanning</span>
                  <p style={{ color: '#00F0FF', fontWeight: 600, fontSize: 12, marginTop: 2, wordBreak: 'break-all' }}>
                    {url.startsWith('http') ? url : 'https://' + url}
                  </p>
                </div>
                <div style={{ borderTop: '1px solid #1a1a3e', paddingTop: 6 }}>
                  <span style={{ fontSize: 10, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 1 }}>Report sent to</span>
                  <p style={{ color: '#b0b8d0', fontSize: 12, marginTop: 2 }}>{email}</p>
                </div>
              </div>

              {/* Pricing – tighter */}
              <div style={{ background: '#020210', border: '1px solid #1a1a3e', borderRadius: 8, padding: isMobile ? '10px 12px' : '12px 14px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#b0b8d0' }}>Website Security Scan</span>
                  <span style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: '#00FF94', fontFamily: 'var(--font-heading)' }}>₹299</span>
                </div>
                {[
                  '8-protocol security analysis',
                  'Professional PDF report',
                  'Security score with grade (A+ to F)',
                  'Prioritised recommendations',
                  'Instant email delivery',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 5 }}>
                    <CheckCircle2 size={12} color="#00FF94" style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 12, color: '#b0b8d0', lineHeight: 1.3 }}>{item}</span>
                  </div>
                ))}
              </div>

              <button onClick={handlePayment} disabled={loading} className="btn-primary"
                data-testid="pay-button"
                style={{
                  width: '100%', fontSize: isMobile ? 14 : 15,
                  padding: isMobile ? '10px 0' : isTablet ? '12px 0' : '11px 0',
                  opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 0 24px rgba(112,0,255,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                {loading ? <><Loader2 size={16} className="animate-spin" />Processing…</> : '🔒  Pay ₹299 & Start Scan'}
              </button>

              <p style={{ textAlign: 'center', color: '#c8c8ce', fontSize: 10, marginTop: 8 }}>
                Secured by Razorpay · UPI · Cards · Net Banking · Wallets
              </p>

              <button onClick={() => { setShowPayment(false); setLoading(false); }} style={{
                display: 'block', width: '100%', background: 'transparent',
                border: '1px solid #1a1a3e', borderRadius: 6,
                padding: isMobile ? '7px 0' : '7px 0',
                color: '#c8c8ce', fontSize: 12, cursor: 'pointer', marginTop: 8,
              }}>
                ← Back to edit details
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          style={{ textAlign: 'center', color: '#d2d2d8', fontSize: 10, marginTop: showPayment ? 6 : 10 }}>
          🔒 256-bit SSL encrypted · Your data is never stored or shared
        </motion.p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .animate-spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
};

export default ScannerPage;